import React from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart, Printer, Truck, Wrench, FileText, ArrowLeft, ArrowUpRight, ShieldCheck } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { TID } from '@/testIds';

const STEPS = [
  {
    n: '01',
    icon: ShoppingCart,
    title: 'Sell',
    tag: 'ON YOUR LETTERHEAD',
    body: 'Quote your client at retail using your brand, your name, your invoice. Use the Wrapulator wholesale mode + Profit Preview to price with confidence.',
    detail: 'Every deal you close belongs to you. Motor iNKS never appears in the transaction chain until you tell us to ship somewhere.',
  },
  {
    n: '02',
    icon: Printer,
    title: 'Print',
    tag: 'CAST MEDIA STANDARDS',
    body: 'Print the wrap on approved cast media (3M 2080 / IJ180C, Avery MPI 1105 or equivalent) with matching cast overlaminate.',
    detail: 'Panel tiling with 0.5"–1.0" vertical overlap for fleet work. Digital proof approved before print run.',
  },
  {
    n: '03',
    icon: Truck,
    title: 'Blind-Ship',
    tag: 'UNBRANDED · UNMARKED',
    body: 'Send the printed panels + optional hardware to our Houston shop, or drop-ship straight from your printer to us. All shipping labels are neutral.',
    detail: 'Boxes arrive unmarked with a generic return address. We accept fleet drop-offs too — coordinate through your account lead.',
  },
  {
    n: '04',
    icon: Wrench,
    title: 'We Install',
    tag: 'MASTER-LEVEL EXECUTION',
    body: 'Certified installers with 15+ years apply the wrap to our zero-failure standard. You get progress photos at intake, mid-install and delivery.',
    detail: 'Difficulty modifier is validated on intake photos before any surcharge — never billed as a surprise. Turnaround: FLT-SM 1 day · FLT-MD 1.0–1.5 days · FLT-LG 1.5–2 days.',
  },
  {
    n: '05',
    icon: FileText,
    title: 'Your Invoice',
    tag: 'YOUR BRAND · YOUR MARGIN',
    body: 'Client picks up (or takes delivery) from your shop and pays you at retail. Motor iNKS bills you at wholesale on Net-30 terms once approved.',
    detail: 'Optional: enroll the wrap in an aftercare MRR plan for recurring revenue every month it stays on the vehicle.',
  },
];

const STANDARDS = [
  'Cast wrap vinyl required — 3M 2080/IJ180C, Avery MPI 1105 or equivalent.',
  'Matching cast overlaminate mandatory on all commercial installs.',
  'Digital proof with panel tiling; 0.5"–1.0" vertical overlap for fleet.',
  'Vehicle arrives washed, no wax or ceramic coating.',
  'Difficulty modifier +25% only when validated on intake photos.',
  'Bulk-tier deductions unlock past 1,500 cumulative sqft per contract.',
];

const FAQS = [
  { q: 'Do you ever contact my client?',
    a: 'No — never. Motor iNKS is entirely white-label. All communication routes through you unless you explicitly loop us in.' },
  { q: 'What about warranty claims down the road?',
    a: 'The Master Execution Guarantee covers edge-lift, bubbling and application-error failures. If you enrolled the wrap in aftercare, the warranty extends for the life of the plan.' },
  { q: 'Do you accept media I print myself in-shop?',
    a: 'Yes — as long as it meets the cast + overlaminate standard. If you print on calendared or unsupported media we may still install with a written condition-of-warranty exception.' },
  { q: 'What are Net-30 terms based on?',
    a: 'Standing partners in good status get Net-30 by default after 3 completed projects. New partners are pay-on-completion until the account line is established.' },
];

export default function HowItWorksPage() {
  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Header />
      <main className="pt-28 pb-20">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
          <Link to="/" className="mi-eyebrow text-white/50 hover:text-white inline-flex items-center gap-2 mb-8">
            <ArrowLeft className="w-3 h-3" /> BACK TO HOME
          </Link>

          <div className="mb-14">
            <div className="mi-eyebrow mb-3 text-[#E63946]">// HOW IT WORKS · B2B PARTNER FLOW</div>
            <h1 className="mi-headline text-5xl md:text-6xl lg:text-7xl">
              Five steps from lead <br />
              <span className="text-[#E63946]">to invoice.</span>
            </h1>
            <p className="mt-6 text-white/75 leading-relaxed max-w-2xl">
              This is the exact loop every Motor iNKS partner runs — from print shops with in-house RIPs to
              agencies who outsource everything. Nothing about your client relationship changes. Only the install
              department gets outsourced.
            </p>
          </div>

          {/* Vertical timeline */}
          <div className="relative">
            <div className="absolute left-[27px] md:left-[35px] top-0 bottom-0 w-px bg-[#E63946]/30" />
            <div className="space-y-8">
              {STEPS.map((s) => (
                <div key={s.n} data-testid={TID.howStep(s.n)} className="relative pl-16 md:pl-24">
                  <div className="absolute left-0 top-0 w-14 h-14 md:w-[70px] md:h-[70px] border-2 border-[#E63946] bg-[#0A0A0A] grid place-items-center">
                    <s.icon className="w-6 h-6 md:w-7 md:h-7 text-[#E63946]" strokeWidth={1.5} />
                  </div>
                  <div className="mi-surface p-6 lg:p-8">
                    <div className="flex flex-wrap items-baseline justify-between gap-3 mb-3">
                      <div className="flex items-baseline gap-4">
                        <span className="font-mono-data text-white/40 text-sm">{s.n}</span>
                        <h2 className="mi-headline text-3xl md:text-4xl">{s.title}</h2>
                      </div>
                      <span className="mi-tag mi-tag-red">{s.tag}</span>
                    </div>
                    <p className="text-white/85 leading-relaxed text-base mb-3">{s.body}</p>
                    <p className="text-white/55 text-sm leading-relaxed">{s.detail}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Standards */}
          <div className="mt-16 mi-surface p-6 lg:p-8 border-l-4 border-l-[#E63946]">
            <div className="flex items-start gap-4">
              <ShieldCheck className="w-7 h-7 text-[#E63946] flex-shrink-0" />
              <div className="w-full">
                <div className="mi-eyebrow mb-1 text-[#E63946]">// PRODUCTION STANDARDS</div>
                <div className="mi-headline text-2xl mb-4">Every install ships to spec.</div>
                <ul className="grid md:grid-cols-2 gap-x-8 gap-y-2 text-sm text-white/80">
                  {STANDARDS.map((s) => (
                    <li key={s} className="flex gap-2"><span className="text-[#E63946]">→</span><span>{s}</span></li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* FAQ */}
          <div className="mt-16">
            <div className="mi-eyebrow mb-3">// PARTNER FAQ</div>
            <h2 className="mi-headline text-4xl md:text-5xl mb-6">
              Straight answers. <span className="text-[#E63946]">No filler.</span>
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              {FAQS.map((f, i) => (
                <div key={i} className="mi-surface p-6">
                  <div className="mi-headline text-xl mb-3">{f.q}</div>
                  <div className="text-sm text-white/75 leading-relaxed">{f.a}</div>
                </div>
              ))}
            </div>
          </div>

          {/* CTA row */}
          <div className="mt-14 mi-surface p-8 lg:p-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 border-l-4 border-l-[#E63946]">
            <div>
              <div className="mi-eyebrow mb-2">// READY TO RUN THE LOOP</div>
              <div className="mi-headline text-3xl">Apply and we'll onboard you in one business day.</div>
            </div>
            <div className="flex gap-3 flex-wrap">
              <Link to="/partners" className="mi-btn-red">
                Apply as Partner <ArrowUpRight className="w-4 h-4" />
              </Link>
              <Link to="/rates" className="mi-btn-ghost">View rate card</Link>
              <Link to="/aftercare" className="mi-btn-ghost">Aftercare MRR</Link>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
